// src/components/Chat AI/AgentReasoningDisplay.js
import React from "react";
import {
  Box,
  SpaceBetween,
  Icon,
  Badge,
  ProgressBar,
  Container,
  Header,
} from "@cloudscape-design/components";

const ReasoningStep = ({ step, index, isActive, isCompleted }) => {
  const getStepIcon = () => {
    if (isCompleted) {
      return <Icon name="status-positive" size="small" variant="success" />;
    }
    if (isActive) {
      return <Icon name="status-in-progress" size="small" variant="normal" />;
    }
    return <Icon name="status-pending" size="small" variant="subtle" />;
  };

  const getStepStatus = () => {
    if (isCompleted) return "completed";
    if (isActive) return "active";
    return "pending";
  };

  return (
    <Box
      padding="s"
      className={`reasoning-step ${getStepStatus()}`}
      style={{
        backgroundColor: isActive ? "#f0f8ff" : isCompleted ? "#f0fff4" : "#f8f9fa",
        border: `1px solid ${
          isActive ? "#0073bb" : isCompleted ? "#16844d" : "#e1e4e8"
        }`,
        borderRadius: "4px",
        marginBottom: "8px",
        opacity: isCompleted || isActive ? 1 : 0.7,
        transition: "all 0.3s ease",
      }}
    >
      <SpaceBetween direction="horizontal" size="s">
        {getStepIcon()}
        <Box style={{ flex: 1 }}>
          <SpaceBetween size="xs">
            <Box
              variant="strong"
              color={isActive ? "text-body-default" : isCompleted ? "text-status-success" : "text-body-secondary"}
            >
              Step {step.stepNumber || index + 1}: {step.title}
            </Box>
            <Box
              variant="small"
              color={isActive ? "text-body-default" : "text-body-secondary"}
            >
              {step.description}
            </Box>
            {step.details && (
              <SpaceBetween direction="horizontal" size="xs">
                {step.details.knowledgeBases && step.details.knowledgeBases.length > 0 && (
                  <Badge color="blue">
                    📚 {step.details.knowledgeBases.join(", ")}
                  </Badge>
                )}
                {step.details.retrievalCount && (
                  <Badge color="purple">
                    📄 {step.details.retrievalCount} sources found
                  </Badge>
                )}
                {step.details.model && (
                  <Badge color="orange">
                    🤖 {step.details.model}
                  </Badge>
                )}
                {step.details.action && step.details.action !== "agent simulation" && step.details.action !== "direct api" && (
                  <Badge color="grey">
                    {step.details.action.replace(/_/g, " ")}
                  </Badge>
                )}
                {step.details.confidence && (
                  <Badge color="green">
                    ✅ {Math.round(step.details.confidence * 100)}% confidence
                  </Badge>
                )}

              </SpaceBetween>
            )}
          </SpaceBetween>
        </Box>
      </SpaceBetween>
    </Box>
  );
};

const AgentReasoningDisplay = ({ 
  reasoningSteps = [], 
  isActive = false, 
  currentStep = 0,
  title = "Agent Reasoning"
}) => {
  if (!reasoningSteps || reasoningSteps.length === 0) {
    return null;
  }

  const completedSteps = reasoningSteps.filter(step => 
    step.status === "completed" || step.status === "success"
  ).length;
  
  const totalSteps = reasoningSteps.length;
  const progressValue = totalSteps > 0 ? (completedSteps / totalSteps) * 100 : 0;

  return (
    <Container
      header={
        <Header 
          variant="h4"
          description={`${completedSteps}/${totalSteps} steps completed`}
        >
          <SpaceBetween direction="horizontal" size="s">
            <Icon name="status-info" size="small" />
            {title}
          </SpaceBetween>
        </Header>
      }
    >
      <SpaceBetween size="m">
        {isActive && (
          <ProgressBar
            value={progressValue}
            additionalInfo={`Processing step ${Math.min(currentStep + 1, totalSteps)} of ${totalSteps}`}
            description="Agent is thinking..."
            variant={progressValue === 100 ? "success" : "normal"}
          />
        )}
        
        <SpaceBetween size="xs">
          {reasoningSteps.map((step, index) => {
            const isStepActive = isActive && index === currentStep;
            const isStepCompleted = step.status === "completed" || 
                                   step.status === "success" || 
                                   index < currentStep;
            
            return (
              <ReasoningStep
                key={step.id || index}
                step={step}
                index={index}
                isActive={isStepActive}
                isCompleted={isStepCompleted}
              />
            );
          })}
        </SpaceBetween>
        
        {!isActive && completedSteps === totalSteps && (
          <Box variant="awsui-key-label" padding="s" color="text-status-success">
            <SpaceBetween direction="horizontal" size="xs">
              <Icon name="status-positive" size="small" variant="success" />
              <Box variant="small">
                Agent reasoning completed successfully
              </Box>
            </SpaceBetween>
          </Box>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default AgentReasoningDisplay;